import React, { type RefObject, forwardRef, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { platform } from 'reactApp/appHelpers/configHelpers';
import { isRebrandingQuotaLanding } from 'reactApp/appHelpers/featuresHelpers';
import { EnvironmentSelectors } from 'reactApp/modules/environment/environment';
import { sendQuotaGa } from 'reactApp/sections/QuotaLanding/QuotaLanding.helpers';
import { FloatingTooltip } from 'reactApp/ui/FloatingTooltip/FloatingTooltip';
import { ETooltipAppearance, ETooltipPlacement, ETooltipSize } from 'reactApp/ui/FloatingTooltip/FloatingTooltip.types';
import { openUnlimitedUploadInfo } from 'reactApp/ui/Mobile/UnlimitedUploadInfo/UnlimitedUploadInfo.helpers';
import { sendXray } from 'reactApp/utils/ga';

import styles from './AutouploadTooltip.css';
import type { AutouploadTooltipProps } from './AutouploadTooltip.types';

export const AUTOUPLOAD_TOOLTIP_ID = 'autoupload-tooltip';

const sendRadar = (action: 'view' | 'click' | 'close') => {
    sendXray(['autoupload_tooltip', platform, action]);
    sendQuotaGa({ action: 'tooltip_autoupload', label: action });
};

export const AutouploadTooltip = forwardRef<HTMLDivElement, AutouploadTooltipProps>(({ onClose }, ref) => {
    const dispatch = useDispatch();
    const isMobile = EnvironmentSelectors.isMobile();

    const handleOnShow = useCallback(() => {
        sendRadar('view');
    }, [dispatch]);

    const handleClose = useCallback(() => {
        onClose?.(false);
        sendRadar('close');
    }, [onClose]);

    const handleClickLink = useCallback(() => {
        sendRadar('click');
        if (isMobile) {
            openUnlimitedUploadInfo();
        }
    }, [isMobile]);

    return (
        <FloatingTooltip
            target={ref as RefObject<HTMLDivElement>}
            placement={ETooltipPlacement.topEnd}
            appearance={ETooltipAppearance.darkInverse}
            size={ETooltipSize.medium}
            onShow={handleOnShow}
            onClose={handleClose}
            allowFlip={false}
            leftOffset={120}
            closeOnOutsideClick
            qaId="autoupload-tooltip"
            usePortal
            belowSticky
            closeOnScroll={!isMobile}
            isRebrandingQuotaLanding={isRebrandingQuotaLanding}
        >
            <div className={styles.root}>
                <div className={styles.text}>
                    Фото и видео с телефона теперь
                    <br />
                    не&nbsp;занимают место в Облаке
                    <br />
                    <a href="#unlimited" className={styles.link} onClick={handleClickLink} rel="noreferrer">
                        Как&nbsp;это&nbsp;работает
                    </a>
                </div>
            </div>
        </FloatingTooltip>
    );
});

AutouploadTooltip.displayName = 'AutouploadTooltip';
